import { IDocument } from 'src/generated-types/document'
import { IMooring, IPositionType } from '../generated-types'
import {
  ICreateLocalityFrequency,
  IUpdateLocalityFrequency,
  IScheduledService,
  IPositionService,
  IUpdatePositionService,
  ILocalityFrequencyGroup,
  ILastAndNextLocalityFrequencies,
  IUpdateScheduledServiceMinimal,
} from '../generated-types/locality-frequency'
import {
  ILocalityFrequenciesSummaryDto,
  IMooringLocalityFrequenciesSummaryDto,
} from 'src/screens/localities/site-services/site-service-types'
import { queryClient } from '../query-client'
import { localityFrequencyQueryKeys } from '../query-keys'
import { apiConsts } from './apiConsts'
import { IPaginatedResult } from './paginated-result'
import { useDelete, useGet, usePatch, usePost, usePut } from './reactQuery'

const baseUrl = (customerId: EntityId, localityId: EntityId) =>
  `customers/${customerId}/localities/${localityId}/locality-frequencies`

export const localityFrequenciesEndpoints = {
  getLocalityFrequencies(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}`
  },
  getAllForLocality(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/getAllForLocality`
  },
  getLocalityFrequency(customerId: EntityId, localityId: EntityId, id: EntityId) {
    return `${baseUrl(customerId, localityId)}/${id}`
  },
  getLocalityFrequencyGroup(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/group`
  },
  getLocalityFrequencyMapPositions(
    customerId: EntityId,
    localityId: EntityId,
    localityFrequencyIds: Array<number | undefined>,
  ) {
    return `${baseUrl(customerId, localityId)}/get-map-positions?localityFrequencyIds=${localityFrequencyIds.join(
      '&localityFrequencyIds=',
    )}`
  },
  getLastAndNextLocalityFrequencies(customerId: EntityId, localityId: EntityId, mooringId: EntityId) {
    return `${baseUrl(customerId, localityId)}/get-last-and-next-locality-services?mooringId=${mooringId}`
  },
  getNextLocalityFrequencies(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/get-next-locality-services`
  },
  createLocalityFrequency(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}`
  },
  deleteLocalityFrequency(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/delete-frequency`
  },
  updateLocalityFrequency(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}`
  },
  getLocalityFrequencyPositions(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/positions`
  },
  getLocalityFrequencyPositionTypes(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/positionTypes`
  },
  groupLocalityFrequencies(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/group-locality-frequencies`
  },
  splitLocalityFrequenciesGroup(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/split-locality-frequencies`
  },
  updateLocalityFrequencyPositions(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/positions`
  },
  updateScheduledServiceMinimal(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/update-minimal`
  },
  deletePositionServiceAttachments(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/position-service-attachments`
  },
  getLocalityFrequencyPositionFiles(customerId: EntityId, localityId: EntityId, positionServiceId: EntityId) {
    return `${baseUrl(customerId, localityId)}/position-service/${positionServiceId}/positions-files`
  },
  getMooringSummary(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/mooring-summary`
  },
  getLocalitySummary(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/locality-summary`
  },
}

export const invalidateLocalityFrequencyCache = {
  getAllLocalityFrequencies(customerId?: EntityId, localityId?: EntityId) {
    queryClient.invalidateQueries(localityFrequencyQueryKeys.GET_LOCALITY_FREQUENCIES(customerId, localityId))
  },
  useGetLocalityFrequency(
    customerId?: EntityId,
    localityId?: EntityId,
    queryParams?: { id?: number | EntityId; groupId?: string },
  ) {
    queryClient.invalidateQueries(
      localityFrequencyQueryKeys.GET_LOCALITY_FREQUENCY(customerId, localityId, queryParams),
    )
  },
  getNextLocalityFrequencies(customerId: EntityId, localityId: EntityId) {
    queryClient.invalidateQueries([localityFrequenciesEndpoints.getNextLocalityFrequencies(customerId, localityId)])
  },
  getLastAndNextFrequencies(customerId: EntityId, localityId: EntityId, mooringId: EntityId) {
    queryClient.invalidateQueries([
      localityFrequenciesEndpoints.getLastAndNextLocalityFrequencies(customerId, localityId, mooringId),
    ])
  },
  getLocalityFrequenciesAndDetails(customerId?: EntityId, localityId?: EntityId) {
    queryClient.invalidateQueries(localityFrequencyQueryKeys.GET_LOCALITY_FREQUENCIES(customerId, localityId))
    queryClient.invalidateQueries(localityFrequencyQueryKeys.GET_LOCALITY_FREQUENCY(customerId, localityId))
  },
  getLocalityFrequencyPositions(customerId: EntityId, localityId: EntityId) {
    queryClient.invalidateQueries([localityFrequenciesEndpoints.getLocalityFrequencyPositions(customerId, localityId)])
  },
  getlocalityFrequencyPositionFiles(customerId: EntityId, localityId: EntityId, positionServiceId: EntityId) {
    queryClient.invalidateQueries(
      localityFrequenciesEndpoints.getLocalityFrequencyPositionFiles(customerId, localityId, positionServiceId),
    )
  },
  getSummaries(customerId?: EntityId, localityId?: EntityId) {
    queryClient.invalidateQueries(localityFrequencyQueryKeys.GET_MOORING_SUMMARY(customerId, localityId))
    queryClient.invalidateQueries(localityFrequencyQueryKeys.GET_LOCALITY_SUMMARY(customerId, localityId))
  },
}

export const useGetLocalityFrequencys = (
  customerId: EntityId,
  localityId: EntityId,
  page: number = apiConsts.Page,
  pageSize: number = apiConsts.PageSize,
  queryParams?: {
    isIgnoreQueryFilter: boolean
    isOnlyCompleted: boolean
    isOnlyOngoing: boolean
  },
  isCustomQueryKey?: boolean,
) =>
  useGet<IPaginatedResult<IScheduledService>>(
    localityFrequenciesEndpoints.getLocalityFrequencies(customerId, localityId),
    {
      page,
      pageSize,
      ...queryParams,
    },
    {
      queryKey: isCustomQueryKey
        ? localityFrequencyQueryKeys.GET_LOCALITY_FREQUENCIES(customerId, localityId, {
            page,
            pageSize: pageSize,
            ...queryParams,
          })
        : localityFrequencyQueryKeys.GET_LOCALITY_FREQUENCIES(customerId),
    },
  )

export const useGetLocalityFrequency = (
  customerId: EntityId,
  localityId: EntityId,
  id: EntityId,
  queryParams?: { isIgnoreQueryFilter?: boolean },
) =>
  useGet<IScheduledService>(
    localityFrequenciesEndpoints.getLocalityFrequency(customerId, localityId, id),
    queryParams,
    { queryKey: localityFrequencyQueryKeys.GET_LOCALITY_FREQUENCY(customerId, localityId, { id }) },
  )

export const useGetLocalityFrequencyGroup = (
  customerId: EntityId,
  localityId: EntityId,
  queryParams: { groupId?: string },
) =>
  useGet<ILocalityFrequencyGroup>(
    localityFrequenciesEndpoints.getLocalityFrequencyGroup(customerId, localityId),
    queryParams,
    {
      queryKey: localityFrequencyQueryKeys.GET_LOCALITY_FREQUENCY(customerId, localityId, {
        groupId: queryParams.groupId,
      }),
    },
  )
export const useGetLocalityFrequencyMapPositions = (
  customerId: EntityId,
  localityId: EntityId,
  localityFrequencyIds: Array<number | undefined>,
) =>
  useGet<IMooring>(
    localityFrequenciesEndpoints.getLocalityFrequencyMapPositions(customerId, localityId, localityFrequencyIds),
  )

export const useAddLocalityFrequency = (customerId: EntityId, localityId: EntityId) =>
  usePost<IScheduledService, ICreateLocalityFrequency>(
    localityFrequenciesEndpoints.createLocalityFrequency(customerId, localityId),
  )

export const useGroupLocalityFrequencies = (customerId: EntityId, localityId: EntityId) =>
  usePatch<IScheduledService, Array<number>>(
    localityFrequenciesEndpoints.groupLocalityFrequencies(customerId, localityId),
  )

export const useSplitLocalityFrequenciesGroup = (customerId: EntityId, localityId: EntityId) =>
  usePatch<IScheduledService, { groupId: string }>(
    localityFrequenciesEndpoints.splitLocalityFrequenciesGroup(customerId, localityId),
  )

export const useDeleteLocalityFrequency = (customerId: EntityId, localityId: EntityId) =>
  useDelete<IScheduledService>(localityFrequenciesEndpoints.deleteLocalityFrequency(customerId, localityId))

export const useGetLocalityFrequencyPositions = (
  customerId: EntityId,
  localityId: EntityId,
  page: number = apiConsts.Page,
  pageSize: number = apiConsts.PageSize,
  queryParams?: { groupId?: string; localityFrequencyId?: EntityId },
) =>
  useGet<IPaginatedResult<IPositionService>>(
    localityFrequenciesEndpoints.getLocalityFrequencyPositions(customerId, localityId),
    {
      ...queryParams,
      page,
      pageSize,
    },
  )

export const useGetAllFrequencyPositionTypes = (
  customerId: EntityId,
  localityId: EntityId,
  queryParams: { groupId?: string; localityFrequencyId?: EntityId },
) =>
  useGet<IPositionType[]>(
    localityFrequenciesEndpoints.getLocalityFrequencyPositionTypes(customerId, localityId),
    queryParams,
  )

export const useUpdateLocalityFrequencyPositions = (customerId: EntityId, localityId: EntityId) => {
  return usePut<IUpdatePositionService, IUpdatePositionService>(
    localityFrequenciesEndpoints.updateLocalityFrequencyPositions(customerId, localityId),
  )
}

export const useUpdateLocalityFrequency = (customerId: EntityId, localityId: EntityId) =>
  usePut<IScheduledService, IUpdateLocalityFrequency>(
    localityFrequenciesEndpoints.updateLocalityFrequency(customerId, localityId),
  )

export const useGetLastAndNextLocalityFrequencies = (customerId: EntityId, localityId: EntityId, mooringId: EntityId) =>
  useGet<ILastAndNextLocalityFrequencies>(
    localityFrequenciesEndpoints.getLastAndNextLocalityFrequencies(customerId, localityId, mooringId),
    undefined,
    { enabled: mooringId != null },
  )

export const useGetNextLocalityFrequencies = (customerId: EntityId, localityId: EntityId) =>
  useGet<ILastAndNextLocalityFrequencies>(
    localityFrequenciesEndpoints.getNextLocalityFrequencies(customerId, localityId),
  )

export const useUpdateScheduledServiceMinimal = (customerId: EntityId, localityId: EntityId) =>
  usePut<IUpdateScheduledServiceMinimal, IUpdateScheduledServiceMinimal>(
    localityFrequenciesEndpoints.updateScheduledServiceMinimal(customerId, localityId),
  )

export const useDeletePositionServiceAttachments = (customerId: EntityId, localityId: EntityId) =>
  useDelete<void>(localityFrequenciesEndpoints.deletePositionServiceAttachments(customerId, localityId))

export const useGetLocalityFrequencyPositionFiles = (
  customerId: EntityId,
  localityId: EntityId,
  positionServiceId: EntityId,
) =>
  useGet<IDocument[]>(
    localityFrequenciesEndpoints.getLocalityFrequencyPositionFiles(customerId, localityId, positionServiceId),
    undefined,
    { enabled: positionServiceId != null },
  )

export const useGetMooringFrequenciesSummary = (customerId: EntityId, localityId: EntityId) =>
  useGet<IMooringLocalityFrequenciesSummaryDto[]>(
    localityFrequenciesEndpoints.getMooringSummary(customerId, localityId),
    undefined,
    { queryKey: localityFrequencyQueryKeys.GET_MOORING_SUMMARY(customerId, localityId) },
  )

export const useGetLocalityFrequenciesSummary = (customerId: EntityId, localityId: EntityId) =>
  useGet<ILocalityFrequenciesSummaryDto>(
    localityFrequenciesEndpoints.getLocalitySummary(customerId, localityId),
    undefined,
    { queryKey: localityFrequencyQueryKeys.GET_LOCALITY_SUMMARY(customerId, localityId) },
  )
